import React, { useEffect, useRef, useState } from 'react';
import ActionPanelButtons from '../../DataTable/ActionPanelButtons';
import StatusActionIcon from '../../Icons/StatusActionIcon';
import { CustomColumn } from '../../../types/customColumn';

interface AdvancedTableBodyProps {
  table: any;
  getActions?: (row: any) => any[];
  noDataMessage?: string;
}

const AdvancedTableBody: React.FC<AdvancedTableBodyProps> = ({
  table,
  getActions,
  noDataMessage,
}) => {
  const { getTableBodyProps, page, prepareRow, columns } = table;

  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);

  const tableRef = useRef<HTMLTableSectionElement>(null);

  /**
   * 🔥 expandir solo si existen acciones
   */
  const handleRowClick = (rowId: number, hasActions: boolean) => {
    if (!hasActions) return;

    setExpandedRowId((prevId) => (prevId === rowId ? null : rowId));
  };

  /**
   * cerrar al hacer click fuera
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tableRef.current &&
        !tableRef.current.contains(event.target as Node)
      ) {
        setExpandedRowId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /**
   * cerrar al cambiar página
   */
  useEffect(() => {
    setExpandedRowId(null);
  }, [page]);

  return (
    <tbody {...getTableBodyProps()} ref={tableRef}>
      {page.length > 0 ? (
        page.map((row: any, rowIndex: number) => {
          prepareRow(row);

          /**
           * 🔥 usar id real
           * no rowIndex
           */
          const rowId = row.original.id || rowIndex;

          const isExpanded = expandedRowId === rowId;

          /**
           * 🔥 calcular acciones una vez
           */
          const actions = getActions ? getActions(row.original) : [];

          const hasActions = actions && actions.length > 0;

          return (
            <React.Fragment key={row.id}>
              <tr
                {...row.getRowProps()}
                className={`
                    dark:border-gray-700
                    transition
                    ${
                      hasActions
                        ? 'hover:bg-gray-50 cursor-pointer dark:hover:bg-graydark/40'
                        : ''
                    }
                  `}
                onClick={() => handleRowClick(rowId, hasActions)}
              >
                {row.cells.map((cell: any) => {
                  const column = cell.column as CustomColumn<object>;

                  /**
                   * 🔥 columnas con status
                   */
                  if (column.showStatus) {
                    return (
                      <td
                        {...cell.getCellProps()}
                        className="
                              text-gray-900
                              dark:text-gray-200
                              px-4
                              py-4
                              align-top
                              text-[15px]
                            "
                      >
                        <div
                          className="
                                flex
                                min-w-max
                                items-start
                                justify-between
                                gap-3
                              "
                        >
                          {/* contenido */}
                          <div className="flex-1">
                            {'customCell' in column && column.customCell
                              ? column.customCell(cell)
                              : cell.render('Cell')}
                          </div>

                          {/* status */}
                          {row.original.status && (
                            <StatusActionIcon value={row.original.status} />
                          )}

                          {/* expand */}
                          {hasActions && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className={`
                                    text-gray-700
                                    dark:text-gray-200
                                    h-5
                                    w-5
                                    transition-transform
                                    duration-300
                                    ${isExpanded ? 'rotate-180' : ''}
                                  `}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.29a.75.75 0 01.02-1.06z"
                              />
                            </svg>
                          )}
                        </div>
                      </td>
                    );
                  }

                  /**
                   * 🔹 columnas normales
                   */
                  return (
                    <td
                      {...cell.getCellProps()}
                      className="
                            text-gray-900
                            dark:text-gray-200
                            px-4
                            py-4
                            align-top
                            text-[15px]
                          "
                    >
                      <div className="min-w-max">
                        {'customCell' in column && column.customCell
                          ? column.customCell(cell)
                          : cell.render('Cell')}
                      </div>
                    </td>
                  );
                })}
              </tr>

              {/* panel expandido */}
              {isExpanded && hasActions && (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="
                          bg-gray-50
                          dark:border-gray-700
                          px-4
                          py-4
                          dark:bg-graydark
                        "
                  >
                    <ActionPanelButtons actions={actions} row={row.original} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })
      ) : (
        <tr>
          <td
            colSpan={columns.length}
            className="
              text-gray-600
              dark:text-gray-300
              border-b
              py-6
              text-center
            "
          >
            {noDataMessage}
          </td>
        </tr>
      )}
    </tbody>
  );
};

export default AdvancedTableBody;
